import React from 'react';

// Actions
import { COMPLETE_QUESTIONNAIRE, refreshOmniRiskProfile, removeEmail } from '@/redux/modules/omni-risk-profiles';
import { startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormattedMessageV2 } from '@/views/components/i18n';

// Enums
import { OmniRiskProfileStatus } from '@/enums/OmniRiskProfiles';

// Hooks
import { useLoadingUIState } from '@/hooks/useLoadingState';

// Libraries
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from '@/redux';

// Types
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';

// Utils
import datadogUtils, { DATADOG_ACTIONS } from '@/utils/3rd-party/datadogUtils';
import notification from '@/utils/notification';
import { formatMessage } from '@/utils/i18n';

type DispatchProps = {
  removeEmail: (email: string, riskProfile: FormattedFullOmniRiskProfile) => void;
  startLoaderAction: (type: string) => void;
  stopLoaderAction: (type: string) => void;
};

type Props = DispatchProps & {
  riskProfile: FormattedFullOmniRiskProfile;
  closeModal: () => void;
};

const CompleteQuestionnaire: React.FC<Props> = ({ riskProfile, closeModal, removeEmail, startLoaderAction, stopLoaderAction }) => {
  const dispatch = useDispatch<Dispatch>();
  const isLoading = useLoadingUIState(COMPLETE_QUESTIONNAIRE.type);

  const handleCompleteQuestionnaire = async () => {
    // We want the loading state to continue even after api call is done
    startLoaderAction(COMPLETE_QUESTIONNAIRE.type);
    if (riskProfile.emails.length && riskProfile.status !== OmniRiskProfileStatus.Approved) {
      try {
        await removeEmail(riskProfile.emails[0].email, riskProfile);
      } catch (err) {
        notification.error(formatMessage('RiskProfileModal.CompleteQuestionnaire.Error'));
        return stopLoaderAction(COMPLETE_QUESTIONNAIRE.type);
      }
    }
    // Dispatch `refreshOmniRiskProfile` with the current `riskProfile` to update its state, reflecting that a refreshed version is now being processed by the advisor.
    if (riskProfile.status === OmniRiskProfileStatus.Approved) {
      await dispatch(refreshOmniRiskProfile(riskProfile));
    }

    datadogUtils.addAction(DATADOG_ACTIONS.RISK_PROFILE.COMPLETE_QUESTIONNAIRE.CONFIRM);

    window.location.href = `${riskProfile.omniFormUrl}/review`; // Redirect to OMNI Form for advisor to complete
  };

  return (
    <>
      <div className="confirmation-modal-content">
        <FormattedMessageV2 className="confirmation-modal-description" toLocalize="RiskProfileModal.CompleteQuestionnaire.Description" />
      </div>
      <div className="confirmation-modal-footer">
        <Button variant="secondary" onClick={closeModal}>
          {formatMessage('RiskProfileModal.CompleteQuestionnaire.Cancel')}
        </Button>
        <Button variant="primary" loading={isLoading} disabled={!riskProfile.omniFormUrl || isLoading} onClick={handleCompleteQuestionnaire}>
          {formatMessage('RiskProfileModal.CompleteQuestionnaire.Submit')}
        </Button>
      </div>
    </>
  );
};

export default connect(null, {
  removeEmail,
  startLoaderAction,
  stopLoaderAction,
})(CompleteQuestionnaire);
